import { useId, useState, useRef, useEffect } from 'react';
import { Checkbox } from './Checkbox';

interface MultiSelectProps {
  onChange: (value: string[]) => void;
  options: { label: string; value: string | number }[];
  label?: string;
  id?: string;
  containerClassName?: string;
  error?: string;
  className?: string;
  value?: string[];
  placeholder?: string;
}

export const MultiSelect = ({
  options,
  onChange,
  label,
  id,
  containerClassName,
  error,
  className,
  value,
  placeholder,
}: MultiSelectProps) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const selectId = id ?? generatedId;

  const handleCheckboxChange = (value: string, checked: boolean) => {
    const newSelectedOptions = checked
      ? [...selectedOptions, value]
      : selectedOptions.filter((opt) => opt !== value);

    setSelectedOptions(newSelectedOptions);
    onChange(newSelectedOptions);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedOptions(value);
    }
  }, [value]);

  const displayText =
    selectedOptions.length === 0 ? placeholder : `Выбрано: ${selectedOptions.length}`;

  return (
    <div className={`w-full ${containerClassName}`} ref={dropdownRef}>
      {label && (
        <label htmlFor={selectId} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          id={selectId}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors bg-white text-left flex justify-between items-center ${
            error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
          } ${className}`}
          onClick={toggleDropdown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="truncate">{displayText}</span>
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div
            className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto"
            role="listbox"
          >
            {options.map((option) => (
              <div
                key={option.value}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
              >
                <Checkbox
                  label={option.label}
                  value={option.value}
                  checked={selectedOptions.includes(option.value as string)}
                  onChange={(e) => handleCheckboxChange(option.value as string, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <select
        multiple
        className="hidden"
        value={selectedOptions}
        onChange={() => {}}
        name={`${selectId}-hidden`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};
